import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntersectionObserver } from '../useIntersectionObserver';
import { useKeepFocusedItemInView } from '../useKeepFocusedItemInView';

type ButtonState = 'none' | 'start' | 'all' | 'end';

type Ref = RefObject<Element | null>;
type UseScrollContainerReturn = ModelHookResult<
	{ scrollRightDisabled: boolean; scrollLeftDisabled: boolean; activeIndex: number },
	{ handleScrollEnd: () => void; scrollToRight: () => void; scrollToLeft: () => void }
>;

// scrollLeft is fractional while scrollWidth/clientWidth are rounded integers, so the edges need a
// tolerance rather than an exact comparison.
const EDGE_TOLERANCE_PX = 1;

function getButtonStateFor(scrollContainer: Element): ButtonState {
	const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
	if (maxScrollLeft <= EDGE_TOLERANCE_PX) return 'none';

	if (scrollContainer.scrollLeft <= EDGE_TOLERANCE_PX) return 'start';
	if (scrollContainer.scrollLeft >= maxScrollLeft - EDGE_TOLERANCE_PX) return 'end';

	return 'all';
}

/**
 * Returns the index encoded in the currently-intersecting entry's id, or 0 if there is none.
 * Each <li> is rendered with id="scroll-container-list-item-{index}" (see ScrollContainerV2), so
 * the currently-intersecting item's own position can be read straight off its id rather than
 * walking the DOM for its sibling index. `id` is read directly off `entry.target` (typed as
 * `Element`, which already declares `id: string`) rather than narrowed via `instanceof
 * HTMLElement` -- that global doesn't exist during SSR, and `instanceof` evaluates its
 * right-hand operand even when the left side is null, throwing ReferenceError server-side
 * (CHANGE-97564).
 */
function getActiveIndex(entry: IntersectionObserverEntry | null): number {
	const match = /-(\d+)$/.exec(entry?.target.id ?? '');
	return match ? Number(match[1]) : 0;
}

// eslint-disable-next-line max-lines-per-function
export function useScrollContainer(scrollContainerRef: Ref): UseScrollContainerReturn {
	const [buttonState, setButtonState] = useState<ButtonState>('none');
	const entry = useIntersectionObserver({ root: scrollContainerRef, threshold: 0.5 });
	const scrollLeftDisabled = buttonState === 'start' || buttonState === 'none';
	const scrollRightDisabled = buttonState === 'end' || buttonState === 'none';
	const activeIndex = useMemo(() => getActiveIndex(entry), [entry]);

	useKeepFocusedItemInView(scrollContainerRef);

	const scrollToLeft = () => {
		if (!scrollContainerRef.current || !entry) return;

		const scrollContainer = scrollContainerRef.current;
		const startPosition = scrollContainer.scrollLeft;

		const currentCard = entry.target;
		const previousCard = currentCard.previousElementSibling;
		const scrollAmount = previousCard ? previousCard.scrollWidth : currentCard.scrollWidth;

		// No explicit `behavior`: that would override the scroller's `motion-safe:scroll-smooth` and
		// animate even for someone who asked for reduced motion.
		scrollContainerRef.current.scrollTo({ left: startPosition - scrollAmount });
	};

	const scrollToRight = () => {
		if (!scrollContainerRef.current || !entry) return;

		const scrollContainer = scrollContainerRef.current;
		const startPosition = scrollContainer.scrollLeft;

		const currentCard = entry.target;
		const nextCard = currentCard.nextElementSibling;
		const scrollAmount = nextCard ? nextCard.clientWidth : currentCard.clientWidth;

		scrollContainer.scrollTo({ left: startPosition + scrollAmount });
	};

	const refreshButtonState = useCallback(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		setButtonState(getButtonStateFor(scrollContainer));
	}, [scrollContainerRef]);

	// initialize button state
	useEffect(() => {
		refreshButtonState();
	}, [refreshButtonState]);

	// Not every scroll ends in a `scrollend` -- one the browser starts itself when something inside
	// takes focus, or any scroll at all on Safari < 17.4, which doesn't implement the event.
	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return undefined;

		scrollContainer.addEventListener('scroll', refreshButtonState, { passive: true });

		return () => {
			scrollContainer.removeEventListener('scroll', refreshButtonState);
		};
	}, [refreshButtonState, scrollContainerRef]);

	return {
		data: { scrollLeftDisabled, scrollRightDisabled, activeIndex },
		actions: { handleScrollEnd: refreshButtonState, scrollToRight, scrollToLeft },
	};
}
